import React from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, CalendarOff, ExternalLink, Loader2, Settings } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import type {
  ChipState, ScheduleStrategySummary,
} from '../../../types/schedule';
import {
  computeStrategyRunState,
  computeStrategySlChip,
  computeStrategyOverallStatus,
  type StrategyRunStateChip,
} from '../../../utils/scheduleChip';
import { formatTimeRange, formatTradingType } from '../../../utils/scheduleSummary';
import { useColumnOrder } from '../../../hooks/useColumnOrder';
import { useAuth } from '../../../context/AuthContext';
import DraggableHeader from '../DraggableHeader';
import { STATUS_VARIANT_CLASSES } from '../../../utils/statusColors';
import PnlFlash from '../../common/PnlFlash';
import EmptyStateWithRedirect from '../../common/EmptyStateWithRedirect';
import { pickScheduleEmpty } from '../../../utils/emptyStates';
import { StatusBoxRow } from '../../common/StatusBoxRow';
import SLPendingPill from '../SLPendingPill';
import ColumnFilterButton from '../ColumnFilterButton';
import BrokerBreakdownLine from '../shared/BrokerBreakdownLine';
import { expiryTypeLabel } from '../../../utils/strategy/expiryDisplay';
import BasketNameLink from '../../basket/BasketNameLink';
import {
  STATUS_OPTIONS,
  STATE_OPTIONS,
  hasActiveScheduleFilters,
  type ScheduleColumnFilters,
  type ScheduleStatusFilter,
  type ScheduleStateFilter,
  type LabeledOption,
} from '../../../utils/scheduleFilters';

type SortColumn =
  | 'attention'     // default — issues first
  | 'time' | 'basketStrategy' | 'state' | 'status' | 'underlying' | 'expiry' | 'product' | 'trading'
  | 'legs' | 'sl' | 'pnl';
type SortDir = 'asc' | 'desc';

type ReorderableColumn = Exclude<SortColumn, 'attention'>;
const DEFAULT_COLUMN_ORDER: readonly ReorderableColumn[] = [
  'time', 'basketStrategy', 'state', 'status', 'underlying', 'expiry', 'product', 'trading',
  'legs', 'sl', 'pnl',
] as const;
const COLUMN_LABELS: Record<ReorderableColumn, string> = {
  time: 'Time',
  basketStrategy: 'Basket / Strategy',
  state: 'State',
  status: 'Health',
  underlying: 'Underlying',
  expiry: 'Expiry',
  product: 'Product',
  trading: 'Trading',
  legs: 'Legs',
  sl: 'SL',
  pnl: 'P&L',
};

/** Sort order for the Health column — errors first, closed last. */
function overallStatusRank(status: string): number {
  switch (status) {
    case 'ERROR':     return 0;
    case 'PENDING':   return 1;
    case 'ACTIVE':    return 2;
    case 'SCHEDULED': return 3;
    case 'CLOSED':    return 4;
    default:          return 5;
  }
}

/** Sort order for the State column — ERROR first, CLOSED last. */
function stateRank(state: string): number {
  switch (state) {
    case 'ERROR':     return 0;
    case 'RUNNING':   return 1;
    case 'SCHEDULED': return 2;
    case 'CLOSED':    return 3;
    default:          return 4;
  }
}

interface ProScheduleTableProps {
  /** Strategies AFTER the view-level search + status filters. */
  strategies: ScheduleStrategySummary[];
  /** Unfiltered day total — used to distinguish "nothing scheduled" from
   *  "nothing matches filters" in the empty state. */
  totalStrategyCount: number;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  columnFilters: ScheduleColumnFilters;
  onColumnFiltersChange: (next: ScheduleColumnFilters) => void;
  filterOptions: {
    baskets:       LabeledOption<string>[];
    underlyings:   LabeledOption<string>[];
    expiries:      LabeledOption<string>[];
    products:      LabeledOption<string>[];
    tradingTypes:  LabeledOption<string>[];
  };
  statusCounts: Record<ScheduleStatusFilter, number>;
  stateCounts: Record<ScheduleStateFilter, number>;
  selectedDate: string;
  isToday: boolean;
  isPast: boolean;
  isTradingDay: boolean;
  currentIstHms: string | null;
  loading?: boolean;
  refreshing?: boolean;
  error?: string | null;
  onOpenStrategy: (groupKey: string) => void;
  onOpenBasket: (basketId: string) => void;
  onEditStrategy: (strategyId: string, basketId: string) => void;
  /** SL placements still awaiting morning re-placement; renders a small pill in the filter bar. */
  slPendingCount?: number;
}

// Canonical palette shared with positions / orders / modals.
const CHIP_CLASSES = STATUS_VARIANT_CLASSES;

function Chip({ state }: { state: ChipState | null }) {
  if (!state) return <span className="text-gray-300 dark:text-gray-600">—</span>;
  return (
    <span
      title={state.subtext || undefined}
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap',
        CHIP_CLASSES[state.variant],
      )}
    >
      {state.label}
    </span>
  );
}

/** State pill shared by the desktop column renderer and the mobile card.
 *  Renders nothing when there's no state so the mobile card can omit the row;
 *  the desktop renderer supplies its own "—" placeholder for column alignment. */
function StateChip({ chip }: { chip: StrategyRunStateChip | null | undefined }) {
  if (!chip) return null;
  return (
    <span
      title={chip.tooltip || undefined}
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap tabular-nums',
        CHIP_CLASSES[chip.variant],
      )}
    >
      {chip.label}
    </span>
  );
}

function keyOf(s: ScheduleStrategySummary): string {
  return `${s.basket_id}#${s.strategy_id}`;
}

function hhmmToMin(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

function hmsToMin(hms: string): number {
  const [h, m] = hms.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

/** Compact relative-time label: "in 2h 14m" / "3h ago" / "now" / null when irrelevant. */
function formatRelativeTime(
  target: string | null, nowHms: string | null, isToday: boolean,
): string | null {
  if (!isToday || !target || !nowHms) return null;
  const t = hhmmToMin(target);
  const n = hmsToMin(nowHms);
  const diff = t - n;
  if (diff === 0) return 'now';
  const abs = Math.abs(diff);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  const text = h > 0 ? `${h}h ${m}m` : `${m}m`;
  return diff > 0 ? `in ${text}` : `${text} ago`;
}

/** Attention rank — lower = more urgent. Issues float to the top by default. */
function attentionRank(
  _s: ScheduleStrategySummary,
  state: StrategyRunStateChip,
  sl: ChipState | null,
): number {
  // Red = 0, Yellow = 1, Green = 2, Gray = 3, nothing = 4
  const variantRankInline = (v: string | undefined): number => {
    switch (v) {
      case 'red': return 0;
      case 'yellow': return 1;
      case 'green': return 2;
      case 'gray': return 3;
      default: return 4;
    }
  };
  return Math.min(
    variantRankInline(state.variant),
    variantRankInline(sl?.variant),
  );
}

/** Colour of the 4px status strip on the left edge of each row. */
function rowStripVariant(
  state: StrategyRunStateChip,
  sl: ChipState | null,
): 'red' | 'yellow' | 'green' | 'gray' {
  const variants: Array<'red' | 'yellow' | 'green' | 'gray'> = [state.variant];
  if (sl) variants.push(sl.variant);
  if (variants.includes('red')) return 'red';
  if (variants.includes('yellow')) return 'yellow';
  if (variants.includes('green')) return 'green';
  return 'gray';
}

const STRIP_CLS: Record<'red' | 'yellow' | 'green' | 'gray', string> = {
  red:    'bg-red-500 dark:bg-red-400',
  yellow: 'bg-yellow-500 dark:bg-yellow-400',
  green:  'bg-green-500 dark:bg-green-400',
  gray:   'bg-gray-200 dark:bg-gray-700',
};

const ProScheduleTable: React.FC<ProScheduleTableProps> = ({
  strategies, totalStrategyCount,
  searchQuery, onSearchChange, columnFilters, onColumnFiltersChange, filterOptions, statusCounts, stateCounts,
  selectedDate, isToday, isPast, isTradingDay, currentIstHms,
  loading = false, refreshing = false, error,
  onOpenStrategy, onOpenBasket, onEditStrategy,
  slPendingCount = 0,
}) => {
  const [sortColumn, setSortColumn] = React.useState<SortColumn>('attention');
  const [sortDir, setSortDir] = React.useState<SortDir>('asc');

  const { user } = useAuth();
  const storageKey = user?.id ? `schedule:pro:column-order:${user.id}` : null;
  const { order: columnOrder, moveColumn, reset: resetColumns, isDefault: columnsAreDefault } =
    useColumnOrder<ReorderableColumn>(storageKey, DEFAULT_COLUMN_ORDER);

  const handleSort = React.useCallback((col: SortColumn) => {
    setSortColumn((prev) => {
      if (prev === col) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDir('asc');
      return col;
    });
  }, []);

  // Wrap in useMemo so downstream useMemos (rowMeta below) don't re-run
  // every render — the object literal otherwise produces a new reference on
  // each render and invalidates the dep array (react-hooks/exhaustive-deps).
  const chipCtx = React.useMemo(
    () => ({ isToday, isPast, nowIstHms: currentIstHms }),
    [isToday, isPast, currentIstHms],
  );

  // `strategies` is already filtered by the view (search + status); compute
  // row meta and sort directly on the incoming list.
  const filtersActive = hasActiveScheduleFilters(searchQuery, columnFilters);

  const setColumnFilter = <K extends keyof ScheduleColumnFilters>(key: K, next: ScheduleColumnFilters[K]) => {
    onColumnFiltersChange({ ...columnFilters, [key]: next });
  };
  /** One-shot reset for every filter + search. Shared between the header
   *  "Clear all" button and the in-table empty-state CTA. */
  const clearAllFilters = React.useCallback(() => {
    onSearchChange('');
    onColumnFiltersChange({
      baskets: new Set(),
      state: new Set(),
      status: new Set(),
      underlyings: new Set(),
      expiries: new Set(),
      products: new Set(),
      tradingTypes: new Set(),
    });
  }, [onSearchChange, onColumnFiltersChange]);

  const rowMeta = React.useMemo(() => {
    const m = new Map<string, {
      state: StrategyRunStateChip;
      sl: ChipState | null;
      strip: 'red' | 'yellow' | 'green' | 'gray';
      rank: number;
    }>();
    for (const s of strategies) {
      const state = computeStrategyRunState(s, chipCtx);
      const sl    = computeStrategySlChip(s);
      m.set(keyOf(s), {
        state, sl,
        strip: rowStripVariant(state, sl),
        rank: attentionRank(s, state, sl),
      });
    }
    return m;
  }, [strategies, chipCtx]);

  const sorted = React.useMemo(() => {
    const mul = sortDir === 'asc' ? 1 : -1;
    const cmp = (a: ScheduleStrategySummary, b: ScheduleStrategySummary): number => {
      const am = rowMeta.get(keyOf(a));
      const bm = rowMeta.get(keyOf(b));
      switch (sortColumn) {
        case 'attention': {
          const d = (am?.rank ?? 9) - (bm?.rank ?? 9);
          if (d !== 0) return d;
          // Tiebreak: time asc
          const at = a.scheduled_entry_time || a.scheduled_exit_time || '99:99';
          const bt = b.scheduled_entry_time || b.scheduled_exit_time || '99:99';
          return at.localeCompare(bt);
        }
        case 'time': {
          const at = a.scheduled_entry_time || a.scheduled_exit_time || '99:99';
          const bt = b.scheduled_entry_time || b.scheduled_exit_time || '99:99';
          return at.localeCompare(bt);
        }
        case 'basketStrategy': {
          const c = a.strategy_name.localeCompare(b.strategy_name);
          return c !== 0 ? c : a.basket_name.localeCompare(b.basket_name);
        }
        case 'state':      return stateRank(am?.state.state ?? 'SCHEDULED') - stateRank(bm?.state.state ?? 'SCHEDULED');
        case 'status':     return overallStatusRank(computeStrategyOverallStatus(a))
          - overallStatusRank(computeStrategyOverallStatus(b));
        case 'underlying': return a.underlying.localeCompare(b.underlying);
        case 'expiry':     return (a.expiry_type || '').localeCompare(b.expiry_type || '');
        case 'product':    return (a.product_type || '').localeCompare(b.product_type || '');
        case 'trading':    return formatTradingType(a.trading_type, a.intraday_exit_mode)
          .localeCompare(formatTradingType(b.trading_type, b.intraday_exit_mode));
        case 'legs':       return (a.total_effective_lots ?? a.total_lots) - (b.total_effective_lots ?? b.total_lots);
        case 'sl':         return variantRank(am?.sl) - variantRank(bm?.sl);
        case 'pnl':        return (a.total_pnl ?? 0) - (b.total_pnl ?? 0);
      }
    };
    return [...strategies].sort((a, b) => cmp(a, b) * mul);
  }, [strategies, sortColumn, sortDir, rowMeta]);

  if (!isTradingDay) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Execution Schedule ({selectedDate})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="py-16 text-center text-sm text-gray-500 dark:text-gray-400">
            Market closed — not a trading day.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3 flex-wrap">
          <CardTitle className="shrink-0 inline-flex items-center gap-2">
            <span>[{sorted.length}] Strategies</span>
            {/* Fixed-width spinner keeps header width stable so the search /
             *  filter row never reflows when refreshing toggles on or off. */}
            <Loader2
              aria-label={refreshing ? 'Refreshing' : undefined}
              className={cn(
                'w-3.5 h-3.5 text-gray-400 shrink-0 transition-opacity',
                refreshing ? 'animate-spin opacity-100' : 'opacity-0',
              )}
            />
          </CardTitle>
          {/* Sort-state indicator — always visible so rows ordering is never
              a mystery. Default "Attention (issues first)" answers the most
              common "why are rows in this order?" question; column-driven
              sorts read e.g. "P&L ↓". */}
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded shrink-0"
            title="Click any column header to sort by that column"
          >
            Sorted by: {sortColumn === 'attention'
              ? 'Attention (issues first)'
              : `${COLUMN_LABELS[sortColumn]} ${sortDir === 'asc' ? '↑' : '↓'}`}
          </span>
          {filtersActive && (
            <button
              type="button"
              onClick={clearAllFilters}
              className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline shrink-0"
              title="Clear search and column filters"
            >
              Clear all
            </button>
          )}
          {sortColumn !== 'attention' && (
            <button
              type="button"
              onClick={() => { setSortColumn('attention'); setSortDir('asc'); }}
              className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline shrink-0"
              title="Reset sort to attention-first (issues at top)"
            >
              Reset sort
            </button>
          )}
          {!columnsAreDefault && (
            <button
              type="button"
              onClick={resetColumns}
              className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline shrink-0"
              title="Restore the default column order"
            >
              Reset columns
            </button>
          )}
          <div className="flex items-center gap-3 ml-auto shrink-0">
            <SLPendingPill count={slPendingCount} />
          </div>
          {/* Phase B3: page-level toolbar above the view owns Search, IST
              clock, Refresh, and Exit All. `searchQuery` / `onSearchChange`
              still arrive as props so `filtersActive` / `clearAllFilters`
              can integrate the search into "Clear all". */}
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {error && (
          <div className="px-6 py-3 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 text-sm text-red-800 dark:text-red-200">
            {error}
          </div>
        )}

        {loading ? (
          <SkeletonRows count={5} />
        ) : totalStrategyCount === 0 ? (
          <EmptyStateWithRedirect
            icon={<CalendarOff className="w-8 h-8" />}
            {...pickScheduleEmpty({ isToday, isPast: !isToday })}
          />
        ) : (
          /* Always render the table when the day has strategies — filters may
           * be hiding everything, but the header + column filter icons must
           * stay reachable so the user can adjust without a round-trip to
           * the global "Clear all" button. */
          <>
            <DesktopTable
              strategies={sorted}
              meta={rowMeta}
              sortColumn={sortColumn}
              sortDir={sortDir}
              onSort={handleSort}
              onOpenStrategy={onOpenStrategy}
              onOpenBasket={onOpenBasket}
              onEditStrategy={onEditStrategy}
              isToday={isToday}
              currentIstHms={currentIstHms}
              columnOrder={columnOrder}
              onMoveColumn={moveColumn}
              columnFilters={columnFilters}
              onSetColumnFilter={setColumnFilter}
              filterOptions={filterOptions}
              statusCounts={statusCounts}
              stateCounts={stateCounts}
              onClearAllFilters={clearAllFilters}
            />
            <MobileCards
              strategies={sorted}
              meta={rowMeta}
              onOpenStrategy={onOpenStrategy}
              onOpenBasket={onOpenBasket}
              onEditStrategy={onEditStrategy}
              isToday={isToday}
              currentIstHms={currentIstHms}
              onClearAllFilters={clearAllFilters}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
};

function variantRank(c: ChipState | null | undefined): number {
  if (!c) return 5;
  switch (c.variant) {
    case 'red': return 0;
    case 'yellow': return 1;
    case 'green': return 2;
    case 'gray': return 3;
    default: return 4;
  }
}

// ── Desktop / Mobile sub-components ─────────────────────────────────────

type RowMetaMap = Map<string, {
  state: StrategyRunStateChip;
  sl: ChipState | null;
  strip: 'red' | 'yellow' | 'green' | 'gray';
  rank: number;
}>;

interface InnerProps {
  strategies: ScheduleStrategySummary[];
  meta: RowMetaMap;
  onOpenStrategy: (groupKey: string) => void;
  onOpenBasket: (basketId: string) => void;
  onEditStrategy: (strategyId: string, basketId: string) => void;
  isToday: boolean;
  currentIstHms: string | null;
  /** Wipes search + every column filter; drives the in-table empty-state CTA. */
  onClearAllFilters: () => void;
}

interface DesktopProps extends InnerProps {
  sortColumn: SortColumn;
  sortDir: SortDir;
  onSort: (col: SortColumn) => void;
  columnOrder: ReorderableColumn[];
  onMoveColumn: (from: ReorderableColumn, to: ReorderableColumn) => void;
  columnFilters: ScheduleColumnFilters;
  onSetColumnFilter: <K extends keyof ScheduleColumnFilters>(key: K, next: ScheduleColumnFilters[K]) => void;
  filterOptions: {
    baskets:       LabeledOption<string>[];
    underlyings:   LabeledOption<string>[];
    expiries:      LabeledOption<string>[];
    products:      LabeledOption<string>[];
    tradingTypes:  LabeledOption<string>[];
  };
  statusCounts: Record<ScheduleStatusFilter, number>;
  stateCounts: Record<ScheduleStateFilter, number>;
}

/** Thin wrapper that picks the correct column filter (or none) for a given Pro header. */
const HeaderFilterSlot: React.FC<{
  col: SortColumn;
  columnFilters: ScheduleColumnFilters;
  onSetColumnFilter: <K extends keyof ScheduleColumnFilters>(key: K, next: ScheduleColumnFilters[K]) => void;
  filterOptions: DesktopProps['filterOptions'];
  statusCounts: Record<ScheduleStatusFilter, number>;
  stateCounts: Record<ScheduleStateFilter, number>;
}> = ({ col, columnFilters, onSetColumnFilter, filterOptions, statusCounts, stateCounts }) => {
  switch (col) {
    case 'basketStrategy':
      return (
        <ColumnFilterButton<string>
          items={filterOptions.baskets}
          selected={columnFilters.baskets}
          onChange={(next) => onSetColumnFilter('baskets', next)}
          title="Filter by basket"
          ariaLabel="Filter basket"
        />
      );
    case 'state':
      return (
        <ColumnFilterButton<ScheduleStateFilter>
          items={STATE_OPTIONS.map((opt) => ({
            value: opt.value,
            label: opt.label,
            count: stateCounts[opt.value],
            leftContent: <span className={cn('w-2 h-2 rounded-full shrink-0', opt.dotClass)} />,
            labelClass: opt.labelClass,
          }))}
          selected={columnFilters.state}
          onChange={(next) => onSetColumnFilter('state', next)}
          title="Filter by state"
          ariaLabel="Filter state"
          searchable={false}
        />
      );
    case 'status':
      return (
        <ColumnFilterButton<ScheduleStatusFilter>
          items={STATUS_OPTIONS.map((opt) => ({
            value: opt.value,
            label: opt.label,
            count: statusCounts[opt.value],
            leftContent: <span className={cn('w-2 h-2 rounded-full shrink-0', opt.dotClass)} />,
            labelClass: opt.labelClass,
          }))}
          selected={columnFilters.status}
          onChange={(next) => onSetColumnFilter('status', next)}
          title="Filter by health"
          ariaLabel="Filter health"
          searchable={false}
        />
      );
    case 'underlying':
      return (
        <ColumnFilterButton<string>
          items={filterOptions.underlyings}
          selected={columnFilters.underlyings}
          onChange={(next) => onSetColumnFilter('underlyings', next)}
          title="Filter by underlying"
          ariaLabel="Filter underlying"
        />
      );
    case 'expiry':
      return (
        <ColumnFilterButton<string>
          items={filterOptions.expiries}
          selected={columnFilters.expiries}
          onChange={(next) => onSetColumnFilter('expiries', next)}
          title="Filter by expiry"
          ariaLabel="Filter expiry"
        />
      );
    case 'product':
      return (
        <ColumnFilterButton<string>
          items={filterOptions.products}
          selected={columnFilters.products}
          onChange={(next) => onSetColumnFilter('products', next)}
          title="Filter by product"
          ariaLabel="Filter product"
        />
      );
    case 'trading':
      return (
        <ColumnFilterButton<string>
          items={filterOptions.tradingTypes}
          selected={columnFilters.tradingTypes}
          onChange={(next) => onSetColumnFilter('tradingTypes', next)}
          title="Filter by trading type"
          ariaLabel="Filter trading type"
        />
      );
    default:
      return null;
  }
};

const SortLabel: React.FC<{
  col: SortColumn; label: string; active: boolean; dir: SortDir;
  onSort: (col: SortColumn) => void;
}> = ({ col, label, active, dir, onSort }) => (
  <button
    type="button"
    onClick={() => onSort(col)}
    onMouseDown={(e) => e.stopPropagation()}
    onDragStart={(e) => e.preventDefault()}
    draggable={false}
    className={cn(
      'inline-flex items-center gap-1 hover:text-gray-700 dark:hover:text-white transition-colors',
      active && 'text-gray-700 dark:text-white',
    )}
  >
    {label}
    {active ? (
      dir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
    ) : (
      <ArrowUpDown className="w-3 h-3 opacity-40" />
    )}
  </button>
);

type ProCellRenderCtx = {
  meta: ReturnType<RowMetaMap['get']>;
  isToday: boolean;
  currentIstHms: string | null;
  groupKey: string;
  onOpenStrategy: (groupKey: string) => void;
  onOpenBasket: (basketId: string) => void;
  /** Strategy-config editor launcher — wired through so the Settings icon
   *  can live inline in the basket/strategy cell instead of an extra column. */
  onEditStrategy: (strategyId: string, basketId: string) => void;
};

const PRO_CELL_RENDERERS: Record<ReorderableColumn, (s: ScheduleStrategySummary, ctx: ProCellRenderCtx) => React.ReactNode> = {
  time: (s, { isToday, currentIstHms }) => {
    const relTime = formatRelativeTime(
      s.scheduled_entry_time || s.scheduled_exit_time,
      currentIstHms, isToday,
    );
    // ORB strategies: `scheduled_entry_time` IS the ORB range-end time —
    // when the monitor evaluates and the order can be placed. Color only the
    // entry time in amber so it reads as the trigger moment, while keeping
    // the → exit time in the standard gray so the strategy's full window is
    // still visible. Amber matches platform-wide ORB colour (entrySource.ts).
    if (s.entry_trigger === 'ORB' && s.scheduled_entry_time) {
      const btstSuffix = s.intraday_exit_mode === 'NEXT_DAY_BTST' ? ' (next day)' : '';
      return (
        <>
          <div className="text-xs tabular-nums">
            <span
              className="text-amber-700 dark:text-amber-300 font-semibold"
              title="ORB end time — order placed at this time"
            >
              {s.scheduled_entry_time}
            </span>
            {s.scheduled_exit_time && (
              <span className="text-gray-700 dark:text-gray-200">
                {' → '}{s.scheduled_exit_time}{btstSuffix}
              </span>
            )}
          </div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-amber-600 dark:text-amber-400 mt-0.5">
            ORB
          </div>
          {relTime && (
            <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">{relTime}</div>
          )}
        </>
      );
    }
    const timeLabel = formatTimeRange(s.scheduled_entry_time, s.scheduled_exit_time, s.intraday_exit_mode);
    return (
      <>
        <div className="text-xs tabular-nums text-gray-700 dark:text-gray-200">{timeLabel}</div>
        {relTime && (
          <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">{relTime}</div>
        )}
      </>
    );
  },
  basketStrategy: (s, { onOpenBasket, onOpenStrategy, onEditStrategy, groupKey }) => {
    // Marketplace subscriptions render in violet (via BasketNameLink) so the
    // subscriber can spot publisher-owned rows at a glance — distinct from the
    // standard blue link colour used for user-created baskets. Click behaviour
    // is unchanged: the basket name still opens the basket overlay via
    // onOpenBasket regardless of source.
    const isMkt = s.is_marketplace === true;
    const strategyClass = isMkt
      ? 'text-violet-700 dark:text-violet-300 hover:text-violet-800 dark:hover:text-violet-200'
      : 'text-blue-700 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200';
    return (
      <div className="flex flex-col gap-0.5 min-w-0">
        <BasketNameLink
          basketId={s.basket_id}
          basketName={s.basket_name || ''}
          isMarketplace={isMkt}
          onClick={() => onOpenBasket(s.basket_id)}
          size="sm"
          title={isMkt ? 'Marketplace subscription' : 'View all strategies in this basket'}
        />
        <div className="flex items-center gap-1.5 min-w-0">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onEditStrategy(s.strategy_id, s.basket_id); }}
            className="p-1 -ml-1 rounded-md text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 dark:hover:text-gray-200 transition-colors shrink-0"
            title="Edit strategy configuration"
            aria-label="Edit strategy configuration"
          >
            <Settings className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onOpenStrategy(groupKey); }}
            className={cn(
              'inline-flex items-center gap-1 text-sm font-semibold hover:underline transition-colors text-left min-w-0',
              strategyClass,
            )}
            title={isMkt ? 'Marketplace strategy — view all legs' : 'View all legs for this strategy'}
          >
            <span className="truncate">{s.strategy_name || '—'}</span>
            <ExternalLink className="w-3 h-3 opacity-40 shrink-0" />
          </button>
        </div>
      </div>
    );
  },
  state: (_s, { meta }) => meta?.state
    ? <StateChip chip={meta.state} />
    : <span className="text-gray-300 dark:text-gray-600">—</span>,
  status: (s) => <StatusBoxRow summary={s} />,
  underlying: (s) => {
    const brokerLine = s.brokers.length === 0
      ? ''
      : s.brokers.length === 1 ? s.brokers[0] : `${s.brokers.length} brokers`;
    return (
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-xs text-gray-700 dark:text-gray-200">{s.underlying}</span>
        {brokerLine && (
          <span
            className="text-[10px] text-gray-400 dark:text-gray-500 truncate"
            title={s.brokers.join(' · ')}
          >
            {brokerLine}
          </span>
        )}
      </div>
    );
  },
  expiry: (s) => (
    <span className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 whitespace-nowrap">
      {s.expiry_type ? expiryTypeLabel(s.expiry_type) : '—'}
    </span>
  ),
  product: (s) => (
    <span className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
      {s.product_type || '—'}
    </span>
  ),
  trading: (s) => (
    <span className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 inline-flex items-center gap-1">
      {formatTradingType(s.trading_type, s.intraday_exit_mode) || '—'}
      {s.expiry_day_rollover && (
        <span
          className="px-1 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-[9px] font-semibold"
          title="Rolls to next contract on the underlying's expiry day"
        >
          ROLLOVER
        </span>
      )}
    </span>
  ),
  legs: (s) => (
    <div className="flex flex-col gap-0.5 min-w-0">
      <span className="text-xs text-gray-700 dark:text-gray-200 tabular-nums">
        {s.leg_count} <span className="text-gray-400">·</span> {s.total_effective_lots ?? s.total_lots}L
      </span>
      <BrokerBreakdownLine items={s.broker_breakdown ?? []} />
    </div>
  ),
  sl:    (_s, { meta }) => <Chip state={meta?.sl ?? null} />,
  pnl: (s) => <PnlFlash value={s.total_pnl} className="text-sm font-semibold" />,
};

const DesktopTable: React.FC<DesktopProps> = ({
  strategies, meta, sortColumn, sortDir, onSort,
  onOpenStrategy, onOpenBasket, onEditStrategy,
  isToday, currentIstHms,
  columnOrder, onMoveColumn,
  columnFilters, onSetColumnFilter, filterOptions, statusCounts, stateCounts,
  onClearAllFilters,
}) => (
  <div className="hidden md:block overflow-x-auto">
    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
      <thead className="bg-gray-50 dark:bg-gray-700">
        <tr>
          {/* Left strip column — anchored, not reorderable */}
          <th className="w-1 p-0" />
          {columnOrder.map((col) => (
            <DraggableHeader key={col} colKey={col} onMove={onMoveColumn}>
              <span className="inline-flex items-center gap-1">
                <SortLabel
                  col={col}
                  label={COLUMN_LABELS[col]}
                  active={sortColumn === col}
                  dir={sortDir}
                  onSort={onSort}
                />
                <HeaderFilterSlot
                  col={col}
                  columnFilters={columnFilters}
                  onSetColumnFilter={onSetColumnFilter}
                  filterOptions={filterOptions}
                  statusCounts={statusCounts}
                  stateCounts={stateCounts}
                />
              </span>
            </DraggableHeader>
          ))}
        </tr>
      </thead>
      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
        {strategies.length === 0 && (
          <tr>
            {/* +1 accounts for the left-strip column. */}
            <td colSpan={columnOrder.length + 1} className="py-16 text-center text-sm text-gray-500 dark:text-gray-400">
              No strategies match the current filters.
              <button
                type="button"
                onClick={onClearAllFilters}
                className="ml-2 font-medium text-blue-600 dark:text-blue-400 hover:underline"
              >
                Clear all filters
              </button>
            </td>
          </tr>
        )}
        {strategies.map((s) => {
          const k = keyOf(s);
          const m = meta.get(k);
          const ctx: ProCellRenderCtx = { meta: m, isToday, currentIstHms, groupKey: k, onOpenStrategy, onOpenBasket, onEditStrategy };

          return (
            <tr
              key={k}
              className="hover:bg-gray-50 dark:hover:bg-gray-700/50 relative"
            >
              <td className="p-0 w-1">
                <div className={cn('w-1 h-full', m ? STRIP_CLS[m.strip] : STRIP_CLS.gray)} style={{ minHeight: 56 }} />
              </td>
              {columnOrder.map((col) => (
                <td key={col} className="px-3 py-3 whitespace-nowrap align-top">
                  {PRO_CELL_RENDERERS[col](s, ctx)}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const MobileCards: React.FC<InnerProps> = ({
  strategies, meta, onOpenStrategy, onOpenBasket, onEditStrategy,
  isToday, currentIstHms, onClearAllFilters,
}) => (
  <div className="md:hidden space-y-2 p-3">
    {strategies.length === 0 && (
      <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
        No strategies match the current filters.
        <button
          type="button"
          onClick={onClearAllFilters}
          className="block mx-auto mt-2 font-medium text-blue-600 dark:text-blue-400 hover:underline"
        >
          Clear all filters
        </button>
      </div>
    )}
    {strategies.map((s) => {
      const k = keyOf(s);
      const m = meta.get(k);
      const relTime = formatRelativeTime(
        s.scheduled_entry_time || s.scheduled_exit_time,
        currentIstHms, isToday,
      );
      return (
        <div
          key={k}
          className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden relative flex"
        >
          <div className={cn('w-1 shrink-0', m ? STRIP_CLS[m.strip] : STRIP_CLS.gray)} />
          <div className="flex-1 p-3 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <BasketNameLink
                  basketId={s.basket_id}
                  basketName={s.basket_name || ''}
                  isMarketplace={s.is_marketplace === true}
                  onClick={() => onOpenBasket(s.basket_id)}
                  size="sm"
                  className="block"
                />
                <button
                  type="button"
                  onClick={() => onOpenStrategy(k)}
                  className={cn(
                    'block text-left text-sm font-semibold truncate hover:underline w-full',
                    s.is_marketplace
                      ? 'text-violet-700 dark:text-violet-300'
                      : 'text-blue-700 dark:text-blue-300',
                  )}
                >
                  {s.strategy_name || '—'}
                </button>
                {m?.state && (
                  <div className="mt-1">
                    <StateChip chip={m.state} />
                  </div>
                )}
              </div>
              <div className="flex items-start gap-2 shrink-0">
                <PnlFlash value={s.total_pnl} className="text-sm font-bold" />
                <button
                  type="button"
                  onClick={() => onEditStrategy(s.strategy_id, s.basket_id)}
                  className="p-1 rounded-md text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                  aria-label="Edit strategy configuration"
                >
                  <Settings className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 flex-wrap">
              <span>
                {s.underlying}
                <span className="ml-1 text-[10px] uppercase text-gray-400 dark:text-gray-500 whitespace-nowrap">
                  {expiryTypeLabel(s.expiry_type)}
                  {s.product_type && ` · ${s.product_type}`}
                </span>
              </span>
              {s.trading_type && (
                <span className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500">
                  · {formatTradingType(s.trading_type, s.intraday_exit_mode)}
                </span>
              )}
              <span>· {s.leg_count} legs · {s.total_effective_lots ?? s.total_lots}L</span>
              <span className="ml-auto tabular-nums">
                {formatTimeRange(s.scheduled_entry_time, s.scheduled_exit_time, s.intraday_exit_mode)}
              </span>
            </div>
            <BrokerBreakdownLine items={s.broker_breakdown ?? []} />
            {relTime && (
              <div className="text-[10px] text-gray-400 dark:text-gray-500">{relTime}</div>
            )}
            {/* Mobile shows the 6-box health row instead of entry/exit chips
             *  — same actionability signal, quarter of the vertical space,
             *  and matches the desktop Health column. */}
            <StatusBoxRow summary={s} />
          </div>
        </div>
      );
    })}
  </div>
);

const SkeletonRows: React.FC<{ count: number }> = ({ count }) => (
  <div className="divide-y divide-gray-200 dark:divide-gray-700">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="h-14 flex items-center gap-4 px-6 animate-pulse">
        <div className="w-1 h-10 bg-gray-200 dark:bg-gray-700 rounded" />
        <div className="h-3 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
        <div className="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded" />
        <div className="h-3 w-32 bg-gray-200 dark:bg-gray-700 rounded" />
        <div className="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded ml-auto" />
      </div>
    ))}
  </div>
);

export default ProScheduleTable;
